import CheckIcon from '@atlaskit/icon/glyph/check';
import { colors as akColors } from '@atlaskit/theme';
import Popup from '@databases/atlaskit/Popup';
import { flaggingCallback, useFlaggingCallback } from '@databases/flags';
import { messages } from '@databases/intl/messages';
import Z_INDEX from '@databases/layout/z-index';
import { SelectOption, selectOptionColors } from '@databases/types/database';
import { selectOptionColorValues } from '@databases/utils/database/field-types/select';
import { isBright } from '@databases/utils/utils';
import React, { FunctionComponent, forwardRef, useState } from 'react';
import { useIntl } from 'react-intl-next';
import styled, { css } from 'styled-components';

const OptionsContainer = styled.div<{ size: number; length: number }>`
  display: grid;
  grid-template-columns: repeat(
    ${(props) => Math.ceil(props.length / 2)},
    ${(props) => props.size}px
  );
  grid-template-rows: 1fr 1fr;
  gap: 4px;
  padding: 4px;
`;

interface Option {
  backgroundColor: string;
  color?: string;
}

export interface ColorPickerProps {
  colors: Option[];
  value: Option;
  onChange: (value: string) => void;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;

  /** The width/height of the picker square in pixels. Defaults to 30. */
  size?: number;

  /** If true, stop the propagation of click events. */
  stopPropagation?: boolean;
}

/**
 * Renders a single option of an Databases select field, styled as an @atlaskit/tag component.
 */
export const ColorPicker: FunctionComponent<ColorPickerProps> = ({
  colors,
  value,
  onChange,
  onFocus,
  onBlur,
  stopPropagation,
  size = 30,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = useFlaggingCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const handleClose = useFlaggingCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <Popup
      onClose={handleClose}
      isOpen={isOpen}
      content={() => (
        <OptionsContainer size={size} length={colors.length}>
          {colors.map((color) => (
            <ColorPickerOption
              key={color.backgroundColor}
              color={color}
              isChecked={color.backgroundColor === value.backgroundColor}
              onClick={flaggingCallback(() => {
                setIsOpen(false);
                onChange(color.backgroundColor);
              })}
              size={size}
              stopPropagation={stopPropagation}
            />
          ))}
        </OptionsContainer>
      )}
      trigger={(triggerProps) => (
        <ColorPickerOption
          {...triggerProps}
          color={value}
          onClick={handleToggle}
          onFocus={onFocus}
          onBlur={onBlur}
          size={size}
          stopPropagation={stopPropagation}
          isOpen={isOpen}
        />
      )}
      placement="bottom-start"
      zIndex={Z_INDEX.COLOR_PICKER}
      autoFocus={!stopPropagation}
    />
  );
};

const hoverColors: Record<string, string> = {
  // Copied from @atlaskit/tag
  [akColors.N20]: akColors.N30,
  [akColors.B100]: akColors.B75,
  [akColors.R100]: akColors.R75,
  [akColors.Y200]: akColors.Y100,
  [akColors.G200]: akColors.G100,
  [akColors.T200]: akColors.T100,
  [akColors.P100]: akColors.P75,
  [akColors.N500]: akColors.N400,
  [akColors.B75]: akColors.B50,
  [akColors.R75]: akColors.R50,
  [akColors.Y100]: akColors.Y75,
  [akColors.G100]: akColors.G75,
  [akColors.T100]: akColors.T75,
  [akColors.P75]: akColors.P50,
  [akColors.N30]: akColors.N40,

  // Needed for dark select option colors
  [akColors.B400]: akColors.B300,
  [akColors.R400]: akColors.R300,
  [akColors.Y300]: akColors.Y200,
  [akColors.G400]: akColors.G300,
  [akColors.P400]: akColors.P300,

  // Needed for light select option colors
  [akColors.B50]: akColors.B75,
  [akColors.R50]: akColors.R75,
  [akColors.Y50]: akColors.Y75,
  [akColors.G50]: akColors.G75,
  [akColors.T50]: akColors.T75,
  [akColors.P50]: akColors.P75,
  [akColors.N40]: akColors.N60,

  // Additionally needed for page status colors
  [akColors.B200]: akColors.B100,
  [akColors.R200]: akColors.R100,
  [akColors.P200]: akColors.P100,
};

const ColorPickerOptionContainer = styled.button<{
  size: number;
  isClickable?: boolean;
  color: string;
  backgroundColor: string;
  borderColor?: string;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: ${(props) => props.size}px;
  width: ${(props) => props.size}px;
  flex-shrink: 0;
  cursor: ${(props) => (props.isClickable ? 'pointer' : 'inherit')};
  border: none;
  color: ${(props) => props.color};
  ${(props) =>
    props.borderColor &&
    css`
      outline: ${`1px solid ${props.borderColor}`};
      outline-offset: -1px;
    `}
  background-color: ${(props) => props.backgroundColor};
  border-radius: 3px;

  &:focus-visible {
    outline: 2px solid #2684ff;
    outline-offset: 2px;
  }

  &:hover {
    ${(props) =>
      hoverColors[props.backgroundColor]
        ? css`
            background-color: ${hoverColors[props.backgroundColor]};
          `
        : css`
            filter: brightness(130%);
          `}
  }
`;

export interface ColorPickerOptionProps {
  color: Option;
  onClick?: () => void;
  isChecked?: boolean;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  size?: number;
  stopPropagation?: boolean;
  isOpen?: boolean;
}

const ColorPickerOption = forwardRef<HTMLSpanElement, ColorPickerOptionProps>(
  function ColorPickerOption(
    { color, onClick, isChecked, isOpen, onFocus, onBlur, stopPropagation, size = 30 },
    ref
  ) {
    const { formatMessage } = useIntl();
    const handleClick = useFlaggingCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        if (stopPropagation) {
          e.stopPropagation();
        }
        onClick?.();
      },
      [onClick, stopPropagation]
    );

    return (
      <ColorPickerOptionContainer
        id="color-picker-option-container"
        onClick={handleClick}
        onFocus={onFocus}
        onBlur={onBlur}
        size={size}
        isClickable={!!onClick}
        color={isBright(color.backgroundColor) ? akColors.N800 : akColors.N0}
        borderColor={
          color.color && isBright(color.backgroundColor, 0.75) ? color.color : undefined
        }
        backgroundColor={color.backgroundColor}
        ref={ref}
        tabIndex={0}
        aria-expanded={isOpen}
      >
        {isChecked && (
          <CheckIcon label={formatMessage(messages['color-picker.selected-label'])} />
        )}
      </ColorPickerOptionContainer>
    );
  }
);

export interface SelectColorPickerProps
  extends Omit<ColorPickerProps, 'colors' | 'value' | 'onChange'> {
  value: SelectOption['color'];
  onChange: (value: SelectOption['color']) => void;
}

const selectOptionColorsOptions = selectOptionColors.map((c) => ({
  backgroundColor: selectOptionColorValues[c][0],
  color: selectOptionColorValues[c][1],
}));

export const SelectColorPicker: FunctionComponent<SelectColorPickerProps> = ({
  value,
  onChange,
  ...props
}) => {
  const handleChange = useFlaggingCallback(
    (value) => {
      const newValue = (
        Object.keys(selectOptionColorValues) as SelectOption['color'][]
      ).find((color) => selectOptionColorValues[color][0] === value);
      if (newValue) {
        onChange(newValue);
      }
    },
    [onChange]
  );

  const defaultValue = {
    backgroundColor: selectOptionColorValues[value][0],
    color: selectOptionColorValues[value][1],
  };

  return (
    <ColorPicker
      colors={selectOptionColorsOptions}
      {...props}
      value={defaultValue}
      onChange={handleChange}
    />
  );
};

export const pageStatusColors = [
  akColors.B200,
  akColors.G200,
  akColors.Y200,
  akColors.R200,
  akColors.P200,
].map((c) => ({ backgroundColor: c }));

export const PageStatusColorPicker: FunctionComponent<
  Omit<ColorPickerProps, 'colors'>
> = (props) => <ColorPicker colors={pageStatusColors} {...props} />;
